/**
 * EVENTSFLO-CREW-PLANNER-SEND-SHIFT-PROJECT-SUMMARY-PDF-V1
 *
 * "Send Summary" button + dialog for the Crew Planner expanded shift panel.
 * Sits next to the existing "ID Pack" button. Lets the admin generate a
 * compact client-facing PDF (single shift or full project) with an optional
 * crew-contact include checkbox, and email it via an editable To / CC /
 * Subject / Body draft (same review pattern as the ID Pack flow).
 *
 * Backend is the source of truth: PDF is rendered server-side via WeasyPrint
 * by shift_project_summary_service.py. The frontend never assembles
 * sensitive data into the PDF.
 */
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import {
  RadioGroup,
  RadioGroupItem,
} from "../ui/radio-group";
import { Loader2, Send, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const isLikelyEmail = (s) =>
  typeof s === "string" && /.+@.+\..+/.test(s.trim());

const parseCcList = (cc) =>
  (cc || "")
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const buildDefaultSubject = (preview, scope) => {
  const proj = (preview?.project_name || "").trim() || "Project";
  if (scope === "project") return `Project Shift Summary - ${proj}`;
  const date = (preview?.shift_date || "").trim();
  return `Shift Summary - ${proj}${date ? ` - ${date}` : ""}`;
};

const buildDefaultBody = (preview, scope, includeCrew) => {
  if (!preview) return "";
  const proj = preview.project_name || "Project";
  const recipientName =
    (preview.client_contact_name && preview.client_contact_name.trim()) ||
    "team";
  const contactsLine = includeCrew
    ? `\nThis version includes assigned crew contact details for operational coordination only. Please do not redistribute beyond the relevant site/client operations team.\n`
    : "";
  return `Hi ${recipientName},

Please find attached the ${scope === "project" ? "project shift" : "shift"} summary for ${proj}. This document is provided for operational planning and verification. Please contact us if anything needs to be updated.
${contactsLine}
Kind regards,
Operations`;
};

export function ShiftSummaryButton({ shiftId, scale = 1 }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Options
  const [scope, setScope] = useState("shift");
  const [includeCrewContacts, setIncludeCrewContacts] = useState(false);

  // Email draft
  const [recipient, setRecipient] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [subjectDirty, setSubjectDirty] = useState(false);
  const [bodyDirty, setBodyDirty] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  // Load preview on open / when scope changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    api
      .get(`/planner/shifts/${shiftId}/summary-pack/preview`, {
        params: { scope },
      })
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
        setRecipient(res.data?.client_email || "");
        if (!subjectDirty) setSubject(buildDefaultSubject(res.data, scope));
        if (!bodyDirty)
          setBody(buildDefaultBody(res.data, scope, includeCrewContacts));
      })
      .catch((err) => {
        if (cancelled) return;
        const detail =
          err?.response?.data?.detail || err.message || "Preview failed";
        toast.error(`Summary preview failed: ${detail}`);
        setOpen(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shiftId, scope]);

  // Refresh default body if include-crew toggle flips and body is not user-edited
  useEffect(() => {
    if (!preview || bodyDirty) return;
    setBody(buildDefaultBody(preview, scope, includeCrewContacts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeCrewContacts]);

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "Summary.pdf";
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    try {
      a.click();
    } catch {
      window.location.href = url;
    }
    setTimeout(() => {
      try {
        if (a.parentNode) a.parentNode.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch {
        /* noop */
      }
    }, 1000);
  };

  const handleDownload = async () => {
    setDownloading(true);
    const toastId = toast.loading("Generating Summary PDF…");
    try {
      const response = await api.get(
        `/planner/shifts/${shiftId}/summary-pack.pdf`,
        {
          params: { scope, include_crew_contacts: includeCrewContacts },
          responseType: "blob",
        }
      );
      let filename = "";
      const disposition = response.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(disposition);
      if (m && m[1]) filename = m[1];
      if (!filename) filename = `Shift_${shiftId}_Summary.pdf`;
      let blob = response.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([response.data], { type: "application/pdf" });
      }
      triggerBlobDownload(blob, filename);
      toast.success(`Summary downloaded (${scope})`, { id: toastId });
    } catch (err) {
      let detail = "";
      try {
        const b = err?.response?.data;
        if (b && typeof b.text === "function") {
          const txt = await b.text();
          try {
            detail = JSON.parse(txt)?.detail || txt;
          } catch {
            detail = txt;
          }
        }
      } catch {
        /* noop */
      }
      if (!detail)
        detail = err?.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Download failed: ${detail}`, { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    const to = recipient.trim();
    if (!to || !isLikelyEmail(to)) {
      toast.error("Enter a valid recipient email before sending");
      return;
    }
    const ccList = parseCcList(cc);
    const badCc = ccList.find((e) => !isLikelyEmail(e));
    if (badCc) {
      toast.error(`CC address is not a valid email: ${badCc}`);
      return;
    }
    const finalSubject = subject.trim();
    if (!finalSubject) {
      toast.error("Subject cannot be empty");
      return;
    }
    if (!body.trim()) {
      toast.error("Email body cannot be empty");
      return;
    }
    setSending(true);
    const toastId = toast.loading("Sending summary to client…");
    try {
      const resp = await api.post(`/planner/shifts/${shiftId}/summary-pack/send`, {
        scope,
        include_crew_contacts: includeCrewContacts,
        recipient_email: to,
        cc_emails: ccList,
        subject: finalSubject,
        body,
      });
      toast.success(
        `Summary sent to ${resp.data?.recipient}${
          ccList.length ? ` (cc: ${ccList.length})` : ""
        }`,
        { id: toastId }
      );
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "";
      if (typeof detail === "string") msg = detail;
      else if (detail && typeof detail === "object")
        msg = detail.message || JSON.stringify(detail);
      else msg = err.message || "Unknown error";
      toast.error(`Send failed: ${msg}`, { id: toastId });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        style={{
          height: `${20 * scale}px`,
          padding: `0 ${6 * scale}px`,
          fontSize: `${10 * scale}px`,
          lineHeight: 1,
          marginLeft: `${4 * scale}px`,
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid="shift-summary-open-btn"
        title="Send shift or project summary PDF to client"
      >
        <Send
          style={{
            width: `${10 * scale}px`,
            height: `${10 * scale}px`,
            marginRight: `${3 * scale}px`,
          }}
        />
        Send Summary
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="shift-summary-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Shift / Project Summary
            </DialogTitle>
            <DialogDescription>
              Generate a compact client-facing PDF and optionally email it to
              the registered client contact. The PDF excludes costs, pay
              rates, supplements, ID documents, compliance, and internal
              notes.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-8 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="space-y-4">
              {/* Scope */}
              <div>
                <Label className="text-sm font-medium">Scope</Label>
                <RadioGroup
                  value={scope}
                  onValueChange={(v) => setScope(v)}
                  className="mt-2 flex gap-6"
                  data-testid="shift-summary-scope"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="shift"
                      id="scope-shift"
                      data-testid="shift-summary-scope-shift"
                    />
                    <Label htmlFor="scope-shift" className="cursor-pointer">
                      Current shift only
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="project"
                      id="scope-project"
                      data-testid="shift-summary-scope-project"
                    />
                    <Label
                      htmlFor="scope-project"
                      className="cursor-pointer"
                    >
                      Full project (
                      {preview?.shift_count ?? "—"} shift
                      {preview?.shift_count === 1 ? "" : "s"})
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Include crew contacts */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="summary-include-crew"
                  checked={includeCrewContacts}
                  onCheckedChange={(v) => setIncludeCrewContacts(!!v)}
                  data-testid="shift-summary-include-crew"
                />
                <div className="-mt-0.5">
                  <Label
                    htmlFor="summary-include-crew"
                    className="cursor-pointer text-sm"
                  >
                    Include assigned crew names and mobile numbers
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Names rendered as LASTNAME, Firstname. Use only when the
                    client / site operations team needs them for coordination.
                  </p>
                </div>
              </div>

              {/* Email draft */}
              <div className="border-t pt-3 space-y-3">
                <div>
                  <Label htmlFor="summary-to" className="text-xs">
                    To
                  </Label>
                  <Input
                    id="summary-to"
                    type="email"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="client@example.com"
                    data-testid="shift-summary-recipient"
                  />
                </div>
                <div>
                  <Label htmlFor="summary-cc" className="text-xs">
                    CC (optional, comma-separated)
                  </Label>
                  <Input
                    id="summary-cc"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="ops@example.com, planner@example.com"
                    data-testid="shift-summary-cc"
                  />
                </div>
                <div>
                  <Label htmlFor="summary-subject" className="text-xs">
                    Subject
                  </Label>
                  <Input
                    id="summary-subject"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setSubjectDirty(true);
                    }}
                    data-testid="shift-summary-subject"
                  />
                </div>
                <div>
                  <Label htmlFor="summary-body" className="text-xs">
                    Body
                  </Label>
                  <Textarea
                    id="summary-body"
                    value={body}
                    onChange={(e) => {
                      setBody(e.target.value);
                      setBodyDirty(true);
                    }}
                    rows={8}
                    data-testid="shift-summary-body"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={loading || downloading || sending}
              data-testid="shift-summary-download-btn"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Download PDF
            </Button>
            <Button
              onClick={handleSend}
              disabled={loading || downloading || sending}
              data-testid="shift-summary-send-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Send to client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
